/**
 * **When a reader has reached a document's last page** — what the PDF player's `ended` is
 * (`../../common/stillEnd.ts`): a document has no playback to finish, so it ends once its last page
 * is reached, and a one-page document once it is displayed.
 *
 * Two ways in, because neither covers the other:
 *
 * - **the current page is the last** — paged to with the toolbar, the page field, a thumbnail, the
 *   outline or a search result, or scrolled until it is the page nearest the middle of the viewer;
 * - **the viewer is scrolled to its end**, which puts the last page on screen when it never becomes
 *   the page nearest the middle: zoomed out, where several pages fit at once, or in two-page mode,
 *   where the left page of the last pair wins the tie. A document the viewer shows whole has no
 *   scroll to make, and is at its end once displayed.
 *
 * Nothing ends before a page of the document has been drawn, and it ends **once per document
 * opened**: scrolling back up and down again, or a page drawn again at another zoom, says nothing
 * more.
 */

/** What the rule reads of the scrolling viewer. */
export interface ScrollBox {
  scrollTop: number
  clientHeight: number
  scrollHeight: number
}

export interface UseReadToEndOptions {
  /** The document's page count, `0` until it is loaded. */
  totalPages: () => number
  /** The page the player counts as current, from `1`. */
  currentPage: () => number
  /** Called once per document opened, when its last page is reached. */
  onEnded: () => void
}

export interface UseReadToEndReturn {
  /** A document is being opened: its end is still to be reached. */
  opened: () => void
  /** A page of it has been drawn; it may already be at its end. */
  displayed: (viewer?: ScrollBox | null) => void
  /** The reader moved — another page, a scroll. */
  check: (viewer?: ScrollBox | null) => void
}

/**
 * Pixels short of the bottom that still count as the end of the scroll: a zoomed page's height is
 * fractional, and the browser rounds `scrollTop` against it.
 */
const SCROLL_END_SLACK_PX = 2

/** Whether the viewer is scrolled to its end. A viewer with no height (hidden) shows nothing. */
function atScrollEnd(viewer: ScrollBox): boolean {
  return (
    viewer.clientHeight > 0 &&
    viewer.scrollTop + viewer.clientHeight >= viewer.scrollHeight - SCROLL_END_SLACK_PX
  )
}

export function useReadToEnd(options: UseReadToEndOptions): UseReadToEndReturn {
  const { totalPages, currentPage, onEnded } = options
  let shown = false
  let ended = false

  function check(viewer?: ScrollBox | null): void {
    if (ended || !shown) return
    const total = totalPages()
    if (total < 1) return
    if (currentPage() >= total || (viewer != null && atScrollEnd(viewer))) {
      ended = true
      onEnded()
    }
  }

  return {
    opened() {
      shown = false
      ended = false
    },
    displayed(viewer) {
      shown = true
      check(viewer)
    },
    check,
  }
}
